import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft, faTableColumns, faSliders, faColumns,
  faCogs, faPalette, faFloppyDisk, faImage, faTrash,
  faFileArrowUp, faSpinner, faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import ContentHeader from "../../../../layout/ContenteHeader";
import TabHeader from "../../../../layout/Tabs/TabHeader";
import TabLink from "../../../../layout/Tabs/TabLink";
import Board from "./Board";
import pipelineApi from "@serie3/freelancesplaceapi/api/crm/pipeline";
import crudApi from "@serie3/common/api/crudApi";
import PipelineConfig from "@serie3/freelancesplaceapi/Config/Crm/PipelineConfig";
import { btn, btnHoverStyle } from "../../../../layout/Buttons/buttonStyles";

const MAPPING_FIELDS = [
  { key: "firstName",   label: "First Name" },
  { key: "lastName",    label: "Last Name" },
  { key: "company",     label: "Company" },
  { key: "position",    label: "Position" },
  { key: "linkedinUrl", label: "LinkedIn URL" },
  { key: "connectedOn", label: "Connected On" },
  { key: "reason",      label: "Reason / Note" },
  { key: "comment",     label: "Comment" },
];

const AUTO_MAP = {
  firstName:   ["First Name", "Vorname", "firstname", "first_name"],
  lastName:    ["Last Name", "Nachname", "Name", "lastname", "last_name"],
  company:     ["Company", "Unternehmen", "Company Name", "Organization"],
  position:    ["Position", "Stelle", "Title", "Job Title"],
  linkedinUrl: ["URL", "LinkedIn URL", "Profile URL", "linkedin_url"],
  connectedOn: ["Connected On", "Verbunden seit", "connected_on"],
  reason:      ["Reason", "Notiz", "Note", "Notes"],
  comment:     ["Comment", "Anmerkung", "Comments"],
};

const prefix = "pipeline-board";

const randomLightColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = Math.floor(Math.random() * 30) + 40;
  const l = Math.floor(Math.random() * 15) + 70;
  const hslToHex = (h, s, l) => {
    s /= 100; l /= 100;
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return "#" + [f(0), f(8), f(4)].map((x) => Math.round(x * 255).toString(16).padStart(2, "0")).join("");
  };
  return hslToHex(h, s, l);
};

const SettingCard = ({ icon, title, description, children }) => (
  <div style={{ background: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "16px", overflow: "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
      <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "#e8f0fe", display: "flex", alignItems: "center", justifyContent: "center", color: "#1a73e8", fontSize: "0.9rem", flexShrink: 0 }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#212121" }}>{title}</div>
        {description && <div style={{ fontSize: "0.8rem", color: "#757575", marginTop: "2px" }}>{description}</div>}
      </div>
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);

const BoardView = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const [name, setName]               = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);

  const [color, setColor]           = useState("#1a73e8");
  const [localColor, setLocalColor] = useState("#1a73e8");
  const [logoPreview, setLogoPreview] = useState(null);
  const [fileName, setFileName]     = useState("");
  const [fileString, setFileString] = useState("");

  const [boardReloadKey, setBoardReloadKey]   = useState(0);
  const [importRows, setImportRows]           = useState(null);
  const [importLoading, setImportLoading]     = useState(false);
  const [importFilter, setImportFilter]       = useState("all");
  const [rowActions, setRowActions]           = useState({});
  const [selectedRows, setSelectedRows]       = useState(new Set());
  const [confirming, setConfirming]           = useState(false);
  const [confirmResult, setConfirmResult]     = useState(null);
  const [pendingFile, setPendingFile]         = useState(null);
  const [csvHeaders, setCsvHeaders]           = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [columnMapping, setColumnMapping]     = useState({});

  const buildConfirmRow = (row, action) => {
    if (action === "link") return { action: "link", existingPersonId: row.match?.personId, reason: row.reason };
    if (action === "create") return { action: "create", vorname: row.vorname, name: row.name, company: row.company, position: row.position, linkedinUrl: row.linkedinUrl, reason: row.reason, connectedOn: row.connectedOn };
    return { action: "skip" };
  };

  const handleConfirmImport = async (rows) => {
    setConfirming(true);
    setConfirmResult(null);
    try {
      const payload = { rows: rows.map((r) => buildConfirmRow(r, rowActions[r.rowIndex])) };
      const res = await crudApi.createNew(`/pipeline/import/confirm/${project.id}`, payload, null, true);
      if (res?.status === 200 || res?.status === 201) {
        setConfirmResult({ ok: true, count: rows.length });
        setBoardReloadKey((k) => k + 1);
        setSelectedRows((prev) => {
          const next = new Set(prev);
          rows.forEach((r) => next.delete(r.rowIndex));
          return next;
        });
        setImportRows((prev) => prev.filter((r) => !rows.some((x) => x.rowIndex === r.rowIndex)));
      } else {
        setConfirmResult({ ok: false });
      }
    } catch (err) {
      console.error("[Pipeline] Confirm import failed:", err);
      setConfirmResult({ ok: false });
    }
    setConfirming(false);
    setTimeout(() => setConfirmResult(null), 3500);
  };

  const confidenceConfig = {
    exact:    { label: "Exact",    bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
    likely:   { label: "Likely",   bg: "#fff8e1", color: "#f57f17", border: "#ffe082" },
    possible: { label: "Possible", bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
    new:      { label: "New",      bg: "#e8f0fe", color: "#1a73e8", border: "#93b4f5" },
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const text = await file.text();
    const firstLine = text.split(/\r?\n/)[0];
    const headers = firstLine.split(",").map((h) => h.trim().replace(/^"|"$/g, "")).filter(Boolean);
    const autoMapping = {};
    MAPPING_FIELDS.forEach(({ key }) => {
      const candidates = AUTO_MAP[key] || [];
      const match = candidates.find((c) => headers.some((h) => h.toLowerCase() === c.toLowerCase()));
      autoMapping[key] = match ? headers.find((h) => h.toLowerCase() === match.toLowerCase()) : "";
    });
    setPendingFile(file);
    setCsvHeaders(headers);
    setColumnMapping(autoMapping);
    setShowMappingModal(true);
  };

  const runCsvPreview = (file, mapping) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      setImportLoading(true);
      setImportRows(null);
      setSelectedRows(new Set());
      const cleanMapping = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      try {
        const res = await crudApi.createNew(
          `/pipeline/import/preview/${project.id}`,
          { file_string: reader.result, file_name: file.name, mapping: cleanMapping },
          null,
          true
        );
        const rows = res?.data ?? [];
        setImportRows(rows);
        const defaults = {};
        rows.forEach((r) => { defaults[r.rowIndex] = r.match ? "link" : "create"; });
        setRowActions(defaults);
      } catch (err) {
        console.error("[Pipeline] CSV preview failed:", err);
      }
      setImportLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const filteredRows = importRows
    ? importRows.filter((r) => {
        if (importFilter === "all")  return true;
        if (importFilter === "new")  return !r.match;
        return r.match?.confidence === importFilter;
      })
    : [];

  const countFor = (key) => {
    if (!importRows) return 0;
    if (key === "all") return importRows.length;
    if (key === "new") return importRows.filter((r) => !r.match).length;
    return importRows.filter((r) => r.match?.confidence === key).length;
  };

  const setRowAction = (rowIndex, action) =>
    setRowActions((prev) => ({ ...prev, [rowIndex]: action }));

  const applyActionToRows = (rows, action) =>
    setRowActions((prev) => {
      const next = { ...prev };
      rows.forEach((r) => { next[r.rowIndex] = action; });
      return next;
    });

  const toggleRow = (rowIndex) =>
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(rowIndex) ? next.delete(rowIndex) : next.add(rowIndex);
      return next;
    });

  const toggleAllFiltered = () => {
    const allSelected = filteredRows.every((r) => selectedRows.has(r.rowIndex));
    if (allSelected) {
      setSelectedRows((prev) => {
        const next = new Set(prev);
        filteredRows.forEach((r) => next.delete(r.rowIndex));
        return next;
      });
    } else {
      setSelectedRows((prev) => {
        const next = new Set(prev);
        filteredRows.forEach((r) => next.add(r.rowIndex));
        return next;
      });
    }
  };

  const selectedFilteredRows = filteredRows.filter((r) => selectedRows.has(r.rowIndex));
  const allFilteredSelected  = filteredRows.length > 0 && filteredRows.every((r) => selectedRows.has(r.rowIndex));

  useEffect(() => {
    const load = async () => {
      const p = await crudApi.show(PipelineConfig.project.show, id);
      if (!p) return;
      setProject(p);
      setName(p.name ?? "");
      setDescription(p.description ?? "");
      const c = p.color || "#1a73e8";
      setColor(c);
      setLocalColor(c);
      if (p.file_name) {
        setFileName(p.file_name);
        const res = await crudApi.getBinary(PipelineConfig.project.filePreview + id);
        if (res?.data) setLogoPreview(URL.createObjectURL(res.data));
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const saveProject = (overrides = {}) =>
    pipelineApi.editProject({
      id: project.id,
      name,
      description,
      color,
      file_name: fileName,
      file_string: fileString,
      ...overrides,
    });

  const handleSave = async () => {
    setSaving(true);
    await saveProject();
    setProject((prev) => ({ ...prev, name, description, color }));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const applyColor = async (hex) => {
    const c = hex || randomLightColor();
    setLocalColor(c);
    setColor(c);
    await saveProject({ color: c });
    setProject((prev) => ({ ...prev, color: c }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const dataUrl = reader.result;
        setLogoPreview(dataUrl);
        await pipelineApi.editProject({
          id: project.id, name, description, color,
          file_name: file.name, file_string: dataUrl,
        });
        setFileName(file.name);
        setFileString(dataUrl);
      } catch (err) {
        console.error("[Pipeline] Logo upload failed:", err);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    try {
      setLogoPreview(null);
      setFileName("");
      setFileString("");
      await pipelineApi.editProject({
        id: project.id,
        name,
        description,
        color,
        file_name: "",
        file_string: "",
      });
    } catch (err) {
      console.error("[Pipeline] Logo remove failed:", err);
    }
  };

  if (loading) {
    return (
      <>
        <ContentHeader>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button onClick={() => navigate("/crm/pipeline")} style={{ background: "none", border: "none", cursor: "pointer", color: "#9e9e9e", fontSize: "1rem", padding: "6px 8px", borderRadius: "8px" }}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div style={{ width: "46px", height: "46px", borderRadius: "12px", background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem" }}>
              <FontAwesomeIcon icon={faTableColumns} />
            </div>
            <div>
              <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#212121", lineHeight: 1.2 }}>Pipeline</div>
              <div style={{ fontSize: "0.85rem", color: "#9e9e9e", marginTop: "2px" }}>CRM · Pipeline</div>
            </div>
          </div>
        </ContentHeader>
        <section className="content" style={{ height: "unset" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px", gap: "12px", color: "#9e9e9e" }}>
            <div style={{ width: "28px", height: "28px", borderRadius: "50%", border: "3px solid #e8f0fe", borderTopColor: "#1a73e8", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </section>
      </>
    );
  }

  const accentColor = color || "#1a73e8";

  return (
    <>
      <style>{btnHoverStyle}</style>

      {/* CSV column mapping modal */}
      <Modal show={showMappingModal} onHide={() => setShowMappingModal(false)} centered size="lg" contentClassName="modal-pipeline-content">
        <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>Map CSV Columns</div>
            <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", marginTop: "2px" }}>{pendingFile?.name} · {csvHeaders.length} columns detected</div>
          </div>
          <button onClick={() => setShowMappingModal(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
        </div>
        <div style={{ background: "#f8f9fb", padding: "0 24px 4px" }}>
          {/* Column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", gap: "0", padding: "14px 0 8px", borderBottom: "2px solid #e8edf5" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#1a73e8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Column in your CSV</div>
            <div />
            <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#424242", textTransform: "uppercase", letterSpacing: "0.06em" }}>Imported as</div>
          </div>
          {/* Rows */}
          {MAPPING_FIELDS.map(({ key, label }) => {
            const mapped = !!columnMapping[key];
            return (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", alignItems: "center", gap: "0", padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                {/* CSV column picker */}
                <select
                  value={columnMapping[key] || ""}
                  onChange={(e) => setColumnMapping((prev) => ({ ...prev, [key]: e.target.value }))}
                  style={{ padding: "7px 10px", border: `1px solid ${mapped ? "#93b4f5" : "#e0e0e0"}`, borderRadius: "8px", fontSize: "0.88rem", background: mapped ? "#f0f4ff" : "#fff", color: mapped ? "#1a73e8" : "#9e9e9e", outline: "none", cursor: "pointer", fontWeight: mapped ? 600 : 400 }}
                >
                  <option value="">— not mapped —</option>
                  {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                {/* Arrow */}
                <div style={{ textAlign: "center", fontSize: "1rem", color: mapped ? "#1a73e8" : "#d0d0d0", fontWeight: 700 }}>→</div>
                {/* Backend field (fixed) */}
                <div style={{ padding: "7px 12px", borderRadius: "8px", background: mapped ? "#e8f0fe" : "#f5f5f5", border: `1px solid ${mapped ? "#93b4f5" : "#e8e8e8"}` }}>
                  <span style={{ fontSize: "0.88rem", fontWeight: 700, color: mapped ? "#212121" : "#9e9e9e" }}>{label}</span>
                </div>
              </div>
            );
          })}
          <div style={{ padding: "12px 0 16px", fontSize: "0.78rem", color: "#9e9e9e" }}>
            Unmapped fields will be skipped · auto-matched {Object.values(columnMapping).filter(Boolean).length} of {MAPPING_FIELDS.length} fields
          </div>
        </div>
        <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 14px 14px" }}>
          <button onClick={() => setShowMappingModal(false)} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
          <button
            onClick={() => { setShowMappingModal(false); runCsvPreview(pendingFile, columnMapping); }}
            style={{ padding: "8px 22px", background: "#1a73e8", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" }}
          >
            Preview Import
          </button>
        </div>
      </Modal>

      <ContentHeader>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <button
            onClick={() => navigate("/crm/pipeline")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9e9e9e", fontSize: "1rem", padding: "6px 8px", borderRadius: "8px", display: "flex", alignItems: "center", transition: "background 0.15s, color 0.15s" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f9ff"; e.currentTarget.style.color = "#1a73e8"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#9e9e9e"; }}
            title="Back to pipelines"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div style={{
            width: "46px", height: "46px", borderRadius: "12px",
            background: logoPreview ? "#f5f5f5" : `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.3rem", fontWeight: 800, flexShrink: 0,
            boxShadow: `0 4px 12px ${accentColor}66`, overflow: "hidden",
          }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              : (project?.name?.charAt(0)?.toUpperCase() || <FontAwesomeIcon icon={faTableColumns} />)
            }
          </div>
          <div>
            <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#212121", lineHeight: 1.2 }}>{project?.name}</div>
            <div style={{ fontSize: "0.85rem", color: "#9e9e9e", marginTop: "2px" }}>CRM · Pipeline</div>
          </div>
        </div>
      </ContentHeader>

      <section className="content" style={{ height: "unset" }}>
        <div className="card" style={{ borderTop: `4px solid ${accentColor}`, overflow: "hidden" }}>

          <TabHeader prefix={prefix}>
            <TabLink prefix={prefix} name="Basics"   icon={faSliders} />
            <TabLink prefix={prefix} name="Steps"    icon={faColumns} />
            <TabLink prefix={prefix} name="Import"   icon={faFileArrowUp} />
            <TabLink prefix={prefix} name="Settings" icon={faCogs} />
          </TabHeader>

          <div className="tab-content" id={`${prefix}-tabContent`}>

            {/* Basics tab */}
            <div className="tab-pane fade show active" id={`${prefix}-Basics`} role="tabpanel" aria-labelledby={`${prefix}-Basics-tab`}>
              <div style={{ padding: "24px", maxWidth: "600px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <label style={{ fontWeight: 600, fontSize: "0.88rem", color: "#424242" }}>Name</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={{ padding: "10px 14px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.92rem", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <label style={{ fontWeight: 600, fontSize: "0.88rem", color: "#424242" }}>Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      placeholder="Optional description…"
                      style={{ padding: "10px 14px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.92rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      onClick={handleSave}
                      disabled={saving || !name.trim()}
                      style={{ padding: "9px 24px", background: saved ? "#2e7d32" : accentColor, color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: saving ? "default" : "pointer", opacity: saving || !name.trim() ? 0.7 : 1, transition: "background 0.2s" }}
                    >
                      {saved ? "Saved!" : saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Steps tab — no padding, board fills edge-to-edge */}
            <div className="tab-pane fade" id={`${prefix}-Steps`} role="tabpanel" aria-labelledby={`${prefix}-Steps-tab`}>
              <Board project={project} reloadKey={boardReloadKey} />
            </div>

            {/* Import tab */}
            <div className="tab-pane fade" id={`${prefix}-Import`} role="tabpanel" aria-labelledby={`${prefix}-Import-tab`}>
              <div style={{ padding: "24px" }}>

                {/* Upload area */}
                <label style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: "12px", padding: "36px 24px",
                  border: "2px dashed #c5d8fb", borderRadius: "12px",
                  background: "#f5f8ff", cursor: "pointer", marginBottom: "24px",
                }}>
                  <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "#e8f0fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FontAwesomeIcon icon={faFileArrowUp} style={{ fontSize: "1.4rem", color: "#1a73e8" }} />
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#212121", marginBottom: "4px" }}>Upload LinkedIn CSV export</div>
                    <div style={{ fontSize: "0.82rem", color: "#9e9e9e" }}>Click to browse · .csv files only</div>
                  </div>
                  <input type="file" accept=".csv,text/csv" onChange={handleCsvUpload} style={{ display: "none" }} />
                </label>

                {importLoading && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#9e9e9e", padding: "16px 0" }}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span style={{ fontSize: "0.9rem" }}>Analyzing CSV…</span>
                  </div>
                )}

                {importRows && !importLoading && (() => {
                  const ActionBtn = ({ row, action, label, color: c, bg, border }) => {
                    const active = rowActions[row.rowIndex] === action;
                    const disabled = action === "link" && !row.match;
                    return (
                      <button
                        disabled={disabled}
                        onClick={() => {
                          if (selectedRows.has(row.rowIndex)) {
                            applyActionToRows(importRows.filter((r) => selectedRows.has(r.rowIndex)), action);
                          } else {
                            setRowAction(row.rowIndex, action);
                          }
                        }}
                        style={{
                          padding: "3px 10px", fontSize: "0.75rem", fontWeight: 600,
                          border: active ? `2px solid ${c}` : "1px solid #e0e0e0",
                          borderRadius: "6px",
                          background: active ? bg : "#fff",
                          color: active ? c : disabled ? "#ccc" : "#757575",
                          cursor: disabled ? "not-allowed" : "pointer",
                          transition: "all 0.12s",
                        }}
                      >{label}</button>
                    );
                  };

                  const BulkBar = ({ rows, label }) => (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.78rem", color: "#757575", fontWeight: 500 }}>{label}:</span>
                      {[
                        { action: "link",   label: "Link",   color: "#1a73e8", bg: "#e8f0fe", border: "#93b4f5" },
                        { action: "create", label: "Create", color: "#2e7d32", bg: "#e8f5e9", border: "#a5d6a7" },
                        { action: "ignore", label: "Ignore", color: "#757575", bg: "#f5f5f5", border: "#e0e0e0" },
                      ].map(({ action, label: al, color: c, bg, border }) => (
                        <button
                          key={action}
                          onClick={() => applyActionToRows(rows, action)}
                          style={{
                            padding: "4px 12px", fontSize: "0.78rem", fontWeight: 700,
                            border: `1px solid ${border}`, borderRadius: "6px",
                            background: bg, color: c, cursor: "pointer",
                          }}
                        >{al}</button>
                      ))}
                    </div>
                  );

                  return (
                    <>
                      {/* Filter chips */}
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
                        {[
                          { key: "all", label: "All" },
                          { key: "new", label: "New" },
                          { key: "exact", label: "Exact" },
                          { key: "likely", label: "Likely" },
                          { key: "possible", label: "Possible" },
                        ].map(({ key, label }) => {
                          const active = importFilter === key;
                          return (
                            <button key={key} onClick={() => setImportFilter(key)} style={{
                              padding: "5px 12px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 600,
                              border: active ? "2px solid #1a73e8" : "1px solid #e0e0e0",
                              background: active ? "#e8f0fe" : "#fff",
                              color: active ? "#1a73e8" : "#424242", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: "6px",
                            }}>
                              {label}
                              <span style={{
                                minWidth: "18px", height: "18px", borderRadius: "9px", fontSize: "0.72rem", padding: "0 4px",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                background: active ? "#1a73e8" : "#f0f0f0",
                                color: active ? "#fff" : "#757575",
                              }}>{countFor(key)}</span>
                            </button>
                          );
                        })}
                      </div>

                      {filteredRows.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px", color: "#9e9e9e", fontSize: "0.9rem" }}>No entries for this filter.</div>
                      ) : (
                        <>
                          {/* Confirm result toast */}
                          {confirmResult && (
                            <div style={{
                              padding: "10px 16px", borderRadius: "8px", marginBottom: "10px", fontSize: "0.85rem", fontWeight: 600,
                              background: confirmResult.ok ? "#e8f5e9" : "#ffebee",
                              color: confirmResult.ok ? "#2e7d32" : "#c62828",
                              border: `1px solid ${confirmResult.ok ? "#a5d6a7" : "#ef9a9a"}`,
                            }}>
                              {confirmResult.ok ? `✓ ${confirmResult.count} row${confirmResult.count !== 1 ? "s" : ""} imported successfully.` : "Import failed. Please try again."}
                            </div>
                          )}

                          {/* Bulk action bar */}
                          <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap",
                            gap: "12px", padding: "10px 14px", marginBottom: "8px",
                            background: selectedFilteredRows.length > 0 ? "#f0f4ff" : "#f8f9fb",
                            borderRadius: "10px",
                            border: selectedFilteredRows.length > 0 ? "1px solid #93b4f5" : "1px solid #eeeeee",
                            transition: "background 0.15s, border-color 0.15s",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                onChange={toggleAllFiltered}
                                style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#1a73e8" }}
                              />
                              <span style={{ fontSize: "0.82rem", color: "#424242", fontWeight: 500 }}>
                                {selectedFilteredRows.length > 0
                                  ? `${selectedFilteredRows.length} of ${filteredRows.length} selected`
                                  : `${filteredRows.length} rows`}
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                              {selectedFilteredRows.length > 0 ? (
                                <>
                                  <BulkBar rows={selectedFilteredRows} label="Set to" />
                                  <div style={{ width: "1px", height: "24px", background: "#d0d0d0" }} />
                                  <button
                                    onClick={() => handleConfirmImport(importRows.filter((r) => selectedRows.has(r.rowIndex)))}
                                    disabled={confirming}
                                    style={{
                                      padding: "6px 16px", borderRadius: "8px", fontSize: "0.82rem", fontWeight: 700,
                                      background: confirming ? "#e8f0fe" : "#1a73e8", color: confirming ? "#1a73e8" : "#fff",
                                      border: "none", cursor: confirming ? "default" : "pointer",
                                      display: "inline-flex", alignItems: "center", gap: "6px",
                                    }}
                                  >
                                    {confirming
                                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Importing…</>
                                      : <>Import {importRows.filter((r) => selectedRows.has(r.rowIndex)).length} selected</>}
                                  </button>
                                </>
                              ) : (
                                <BulkBar rows={filteredRows} label="Set all filtered to" />
                              )}
                            </div>
                          </div>

                          {/* Table */}
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                              <thead>
                                <tr style={{ borderBottom: "2px solid #f0f0f0" }}>
                                  <th style={{ padding: "10px 12px", width: "32px" }} />
                                  {["Name", "Company", "Position", "LinkedIn", "Connected", "Match", "Action"].map((h) => (
                                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#424242", whiteSpace: "nowrap", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredRows.map((row, i) => {
                                  const conf = row.match?.confidence;
                                  const cfg = conf ? confidenceConfig[conf] : confidenceConfig.new;
                                  const isSelected = selectedRows.has(row.rowIndex);
                                  const action = rowActions[row.rowIndex];
                                  return (
                                    <tr
                                      key={row.rowIndex ?? i}
                                      style={{
                                        borderBottom: "1px solid #f5f5f5",
                                        background: isSelected ? "#f0f4ff" : action === "ignore" ? "#fafafa" : "",
                                        opacity: action === "ignore" ? 0.5 : 1,
                                        transition: "background 0.1s, opacity 0.1s",
                                      }}
                                    >
                                      <td style={{ padding: "10px 12px" }}>
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleRow(row.rowIndex)}
                                          style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: "#1a73e8" }}
                                        />
                                      </td>
                                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap", fontWeight: 600, color: "#212121" }}>
                                        {[row.vorname, row.name].filter(Boolean).join(" ") || "—"}
                                      </td>
                                      <td style={{ padding: "10px 12px", color: "#424242", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {row.company || "—"}
                                      </td>
                                      <td style={{ padding: "10px 12px", color: "#424242", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {row.position || "—"}
                                      </td>
                                      <td style={{ padding: "10px 12px" }}>
                                        {row.linkedinUrl
                                          ? <a href={row.linkedinUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#1a73e8", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "0.78rem" }}>
                                              <FontAwesomeIcon icon={faExternalLinkAlt} /> LinkedIn
                                            </a>
                                          : "—"}
                                      </td>
                                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#757575" }}>
                                        {row.connectedOn || "—"}
                                      </td>
                                      <td style={{ padding: "10px 12px" }}>
                                        <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "12px", fontSize: "0.75rem", fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                                          {conf ? cfg.label : "New"}
                                        </span>
                                        {row.match && (
                                          <div style={{ fontSize: "0.72rem", color: "#9e9e9e", marginTop: "3px" }}>
                                            {[row.match.vorname, row.match.name].filter(Boolean).join(" ")}
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ padding: "10px 12px" }}>
                                        <div style={{ display: "flex", gap: "4px" }}>
                                          <ActionBtn row={row} action="link"   label="Link"   color="#1a73e8" bg="#e8f0fe" border="#93b4f5" />
                                          <ActionBtn row={row} action="create" label="Create" color="#2e7d32" bg="#e8f5e9" border="#a5d6a7" />
                                          <ActionBtn row={row} action="ignore" label="Ignore" color="#757575" bg="#f5f5f5" border="#e0e0e0" />
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}

              </div>
            </div>

            {/* Settings tab */}
            <div className="tab-pane fade" id={`${prefix}-Settings`} role="tabpanel" aria-labelledby={`${prefix}-Settings-tab`}>
              <div style={{ padding: "24px", maxWidth: "600px" }}>

                <SettingCard
                  icon={faPalette}
                  title="Pipeline Color"
                  description="Used for the pipeline accent and header border"
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input
                        type="color"
                        value={localColor}
                        onChange={(e) => setLocalColor(e.target.value)}
                        onBlur={(e) => applyColor(e.target.value)}
                        style={{ width: "48px", height: "48px", border: "2px solid #e0e0e0", borderRadius: "10px", padding: "2px", cursor: "pointer", background: "none" }}
                      />
                      <div style={{ width: "80px", height: "48px", borderRadius: "10px", background: localColor, border: "1px solid rgba(0,0,0,0.08)" }} />
                      <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#424242" }}>{localColor}</div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        className="btn-modern"
                        style={{ ...btn.base, ...btn.secondary, fontSize: "0.78rem", padding: "4px 10px" }}
                        onClick={() => applyColor(null)}
                      >
                        <FontAwesomeIcon icon={faPalette} /> Random color
                      </button>
                      <button
                        type="button"
                        className="btn-modern"
                        style={{ ...btn.base, ...btn.primary, fontSize: "0.78rem", padding: "4px 10px" }}
                        onClick={() => applyColor(localColor)}
                      >
                        <FontAwesomeIcon icon={faFloppyDisk} /> Save color
                      </button>
                    </div>
                  </div>
                </SettingCard>

                <SettingCard
                  icon={faImage}
                  title="Pipeline Logo"
                  description="Upload a logo to identify this pipeline visually"
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    {logoPreview ? (
                      <img src={logoPreview} alt="Pipeline logo" style={{ width: "72px", height: "72px", objectFit: "contain", borderRadius: "10px", border: "1px solid #e0e0e0", background: "#fafafa" }} />
                    ) : (
                      <div style={{ width: "72px", height: "72px", borderRadius: "10px", border: "2px dashed #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", color: "#bdbdbd" }}>
                        <FontAwesomeIcon icon={faImage} style={{ fontSize: "1.6rem" }} />
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <label style={{ ...btn.base, ...btn.primary, fontSize: "0.78rem", padding: "5px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <FontAwesomeIcon icon={faImage} /> {logoPreview ? "Change Logo" : "Upload Logo"}
                        <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                      </label>
                      {logoPreview && (
                        <button
                          type="button"
                          className="btn-modern"
                          style={{ ...btn.base, ...btn.danger, fontSize: "0.78rem", padding: "5px 12px" }}
                          onClick={removeLogo}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                </SettingCard>

              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default BoardView;
